import { cn } from "./cn";

/**
 * Port of the reference's Skeleton (src/components/ui/Skeleton.tsx):
 * Skeleton, TableSkeleton, StatCardSkeleton. No reference test —
 * Skeleton.test.tsx below is authored.
 */

interface SkeletonProps {
  className?: string;
  variant?: "default" | "card";
}

export function Skeleton({ className = "h-4 w-32", variant }: SkeletonProps) {
  if (variant === "card") {
    return (
      <div
        className="h-32 w-full rounded-xl bg-elevated animate-pulse"
        role="status"
        aria-label="Loading"
      />
    );
  }

  return (
    <div
      className={cn("rounded-lg bg-elevated animate-pulse", className)}
      role="status"
      aria-label="Loading"
    />
  );
}

/* ── Table skeleton ─────────────────────────────────────────── */

interface TableSkeletonProps {
  columns?: number;
  rows?: number;
}

const CELL_WIDTHS = ["w-24", "w-32", "w-16", "w-20", "w-28", "w-36", "w-14"];

function cellWidth(row: number, col: number): string {
  return CELL_WIDTHS[(row * 7 + col * 3) % CELL_WIDTHS.length];
}

export function TableSkeleton({ columns = 5, rows = 5 }: TableSkeletonProps) {
  return (
    <div
      className="app-panel rounded-xl overflow-hidden"
      role="status"
      aria-label="Loading table"
    >
      <table className="w-full">
        <thead>
          <tr className="border-b border-edge">
            {Array.from({ length: columns }, (_, col) => (
              <th key={col} className="px-4 py-3 text-left">
                <div
                  className={`h-3 rounded-lg bg-elevated animate-pulse ${cellWidth(0, col)}`}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, row) => (
            <tr
              key={row}
              className={row < rows - 1 ? "border-b border-edge" : ""}
            >
              {Array.from({ length: columns }, (_, col) => (
                <td key={col} className="px-4 py-3">
                  <div
                    className={`h-3 rounded-lg bg-elevated animate-pulse ${cellWidth(row + 1, col)}`}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Stat card skeleton ─────────────────────────────────────── */

export function StatCardSkeleton() {
  return (
    <div
      className="app-panel rounded-xl p-5"
      role="status"
      aria-label="Loading stat card"
    >
      <div className="w-8 h-8 rounded-lg bg-elevated animate-pulse" />
      <div className="mt-3 h-3 w-20 rounded-lg bg-elevated animate-pulse" />
      <div className="mt-2 h-6 w-28 rounded-lg bg-elevated animate-pulse" />
    </div>
  );
}
